
import ComponentInitializeDispatcher from '@owc-utils/Mixins/ComponentInitializeDispatcher';
import { FeatureSliderData } from './FeatureSlider';
import { THROTTLE_LIMIT, OVERLAPPING_THRESHOLD } from './FeatureSlider.config';
import { throttle } from '@owc-utils/Events/Throttle';
import { createEventListener } from '@owc-utils/Mixins/EventListenerMixin';
/**
 * `owc-stage-feature-slider` was introduced because the wb-content-slider is intended to be
 * used in full width only. Trying to force it to fit into a container lead to errors.
 * This might be replaced in the future.
 */
export default ComponentInitializeDispatcher.extend({
  name: 'owc-feature-slider',
  props: {
    isDarkMode: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  mixins: [
    createEventListener('resize', 'handleWindowResize', {
      throttleLimit: THROTTLE_LIMIT,
    }),
  ],
  data: () =>
    ({
      isOverlapping: false,
      overlappingSlideCount: 0,
      currentSlide: 0,
      throttledScrollListener: null,
      isSingleChild: false,
    } as FeatureSliderData),
  computed: {
    slides(): Element[] {
      const sliderContent = this.$refs.sliderContent as Element;
      return sliderContent ? Array.from(sliderContent.children) : [];
    },
    theme(): string {
      return this.isDarkMode ? 'dark' : 'light';
    },
    isDotNavigationVisible(): boolean {
      return this.isOverlapping && this.overlappingSlideCount > 0;
    },
  },
  mounted(): void {
    this.handleWindowResize();
    this.isSingleChild = this.slides.length === 1;
    this.throttledScrollListener = throttle(THROTTLE_LIMIT, this.handleScrolling);
  },
  methods: {
    checkOverlapping(): boolean {
      const sliderContent = this.$refs.sliderContent as HTMLElement;

      if (!sliderContent) return false;
      return sliderContent.scrollWidth - sliderContent.clientWidth > OVERLAPPING_THRESHOLD;
    },
    handleWindowResize(): void {
      this.isOverlapping = this.checkOverlapping();
      if (this.isOverlapping) {
        this.calculateOverlappedSlides();
        this.findCurrentSlide();
      }
    },
    /**
     * Handle the scrolling of the slider content. When scrolling occures
     * the dot navigation needs to be updated.
     */
    handleScrolling(): void {
      // The slide is considered as "active" when more then 1/4 of its width
      // is covered. Thes leads to issue when the screensize is big enought
      // to display two slides or one slide with full with and another slide
      // with width coveres less then 1/4 of its width.
      this.findCurrentSlide();

      // To solve this issue the scrollbar is used to check the scroll distance
      // to the end of the content. If the scorllbar reaches the end the
      // current active slider will be overwritten to the last element of the
      // slides array
      const sliderContent = this.$refs.sliderContent as HTMLElement;
      if (!sliderContent) return;

      const { scrollWidth, clientWidth, scrollLeft } = sliderContent;
      const scrollableDistance = scrollWidth - clientWidth;

      if (scrollLeft > scrollableDistance - OVERLAPPING_THRESHOLD) {
        this.currentSlide = this.slides.length;
      }
    },
    /**
     * find the left most slide and figure out its index within the `slides` array.
     */
    findCurrentSlide(): void {
      const wrapperBorder = this.getSliderBorders();
      const mostLeftSlide = this.slides.find(slide => {
        const { x, width } = slide.getBoundingClientRect();
        // My idea is items/slides that has 1/4 of its width covered
        // doesn't count as active/visible anymore
        return x + width * 0.25 >= wrapperBorder.left;
      }) as HTMLElement;
      // since the first element of the dot navigation is a 1 and not a 0
      // we need to add 1 to the index.
      this.currentSlide = this.slides.indexOf(mostLeftSlide) + 1;
    },
    /**
     * The overlapping calculation is quite simple. Just check if the left corner is below
     * or the right corner is above the bounce of the wrapper element.
     */
    calculateOverlappedSlides(): void {
      const wrapperBorder = this.getSliderBorders();
      const overlappedItems = this.slides.filter(slide => {
        const { x, width } = slide.getBoundingClientRect();
        const overlappingLeft = x + OVERLAPPING_THRESHOLD <= wrapperBorder.left;
        const overlappingRight = x + width - OVERLAPPING_THRESHOLD >= wrapperBorder.right;
        return overlappingLeft || overlappingRight;
      });
      this.overlappingSlideCount = overlappedItems.length + 1;
    },
    /**
     * Figure out the left and right border of the container.
     * This is necessary for the calculation of overlapping slides.
     */
    getSliderBorders(): { left: number; right: number } {
      const sliderWrapper = this.$refs.sliderWrapper as HTMLElement;
      let left = 0;
      let right = 0;
      if (sliderWrapper) {
        const { x, width } = sliderWrapper.getBoundingClientRect();
        left = x;
        right = x + width;
      }
      return {
        left,
        right,
      };
    },
    /**
     * Listen to the change event of wb-dot-navigation and scroll to
     * corresponding slide position.
     */
    onDotNavigationChanged(wbchangeEvent: CustomEvent): void {
      const dotNavigationPositon = wbchangeEvent.detail;
      const slideToShow = this.slides[dotNavigationPositon - 1];
      const sliderContent = this.$refs.sliderContent as HTMLElement;
      if (sliderContent) {
        const { width } = slideToShow.getBoundingClientRect();
        sliderContent.scrollLeft = width * (dotNavigationPositon - 1);
      }
    },
  },
});
